import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, FileTextIcon } from 'lucide-react';
export function BillApprovalWizard() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [step, setStep] = useState(1);
  const [approvalDecision, setApprovalDecision] = useState<'approve' | 'reject' | null>(null);
  const [notes, setNotes] = useState('');
  const [verificationChecks, setVerificationChecks] = useState({
    supplierVerified: false,
    amountVerified: false,
    itemsVerified: false,
    termsVerified: false
  });
  // Mock bill data
  const bill = {
    id: id,
    billNumber: 'BILL-2024-001',
    supplierName: 'Premium Tea Estates Ltd',
    amount: 125000,
    dueDate: '2024-02-15',
    issueDate: '2024-01-15',
    description: 'Ceylon Black Tea - January Order',
    items: [{
      id: '1',
      description: 'Ceylon Black Tea - Grade A',
      quantity: 500,
      unitPrice: 200,
      total: 100000
    }, {
      id: '2',
      description: 'Packaging & Handling',
      quantity: 1,
      unitPrice: 25000,
      total: 25000
    }]
  };
  const handleCheckboxChange = (key: keyof typeof verificationChecks) => {
    setVerificationChecks({
      ...verificationChecks,
      [key]: !verificationChecks[key]
    });
  };
  const allChecksCompleted = Object.values(verificationChecks).every(check => check);
  const handleSubmit = () => {
    console.log('Submitting approval:', {
      billId: id,
      decision: approvalDecision,
      notes,
      verificationChecks
    });
    navigate('/bills');
  };
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-2735">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-2736">
        <button onClick={() => navigate(`/bill-detail/${id}`)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-2737">
          <ArrowLeftIcon size={20} data-id="element-2738" />
          Back to Bill
        </button>
      </div>
      {/* Progress Steps */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2739">
        <div className="flex items-center justify-between mb-8" data-id="element-2740">
          <div className="flex-1" data-id="element-2741">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2742">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-2743">
                1
              </div>
              <div className="ml-3" data-id="element-2744">
                <div className="text-sm font-medium" data-id="element-2745">Review Bill</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-2746"></div>
          <div className="flex-1" data-id="element-2747">
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2748">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-2749">
                2
              </div>
              <div className="ml-3" data-id="element-2750">
                <div className="text-sm font-medium" data-id="element-2751">Verification</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-2752"></div>
          <div className="flex-1" data-id="element-2753">
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2754">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-2755">
                3
              </div>
              <div className="ml-3" data-id="element-2756">
                <div className="text-sm font-medium" data-id="element-2757">Decision</div>
              </div>
            </div>
          </div>
        </div>
        {/* Step 1: Review Bill */}
        {step === 1 && <div className="space-y-6" data-id="element-2758">
            <div data-id="element-2759">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2760">
                Review Bill Details
              </h2>
              <p className="text-gray-600" data-id="element-2761">
                Please review the bill information carefully before proceeding
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-2762">
              <div className="grid grid-cols-2 gap-4" data-id="element-2763">
                <div data-id="element-2764">
                  <div className="text-sm text-gray-500" data-id="element-2765">Bill Number</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-2766">
                    {bill.billNumber}
                  </div>
                </div>
                <div data-id="element-2767">
                  <div className="text-sm text-gray-500" data-id="element-2768">Supplier</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-2769">
                    {bill.supplierName}
                  </div>
                </div>
                <div data-id="element-2770">
                  <div className="text-sm text-gray-500" data-id="element-2771">Issue Date</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-2772">
                    {new Date(bill.issueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div data-id="element-2773">
                  <div className="text-sm text-gray-500" data-id="element-2774">Due Date</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-2775">
                    {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
              </div>
              <div data-id="element-2776">
                <div className="text-sm text-gray-500 mb-2" data-id="element-2777">Description</div>
                <div className="text-base text-gray-900" data-id="element-2778">
                  {bill.description}
                </div>
              </div>
              <div data-id="element-2779">
                <div className="text-sm text-gray-500 mb-3" data-id="element-2780">Items</div>
                <div className="space-y-2" data-id="element-2781">
                  {bill.items.map(item => <div key={item.id} className="flex justify-between items-center p-3 bg-white rounded border border-gray-200" data-id="element-2782">
                      <div data-id="element-2783">
                        <div className="text-sm font-medium text-gray-900" data-id="element-2784">
                          {item.description}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-2785">
                          {item.quantity} × Rs.{' '}
                          {item.unitPrice.toLocaleString('en-LK')}
                        </div>
                      </div>
                      <div className="text-sm font-medium text-gray-900" data-id="element-2786">
                        Rs. {item.total.toLocaleString('en-LK')}
                      </div>
                    </div>)}
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-2787">
                <div className="flex justify-between items-center" data-id="element-2788">
                  <span className="text-lg font-semibold text-gray-900" data-id="element-2789">
                    Total Amount
                  </span>
                  <span className="text-2xl font-bold text-gray-900" data-id="element-2790">
                    Rs. {bill.amount.toLocaleString('en-LK')}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-2791">
              <button onClick={() => setStep(2)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-2792">
                Continue to Verification
              </button>
            </div>
          </div>}
        {/* Step 2: Verification */}
        {step === 2 && <div className="space-y-6" data-id="element-2793">
            <div data-id="element-2794">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2795">
                Verification Checklist
              </h2>
              <p className="text-gray-600" data-id="element-2796">
                Please verify the following items before making a decision
              </p>
            </div>
            <div className="space-y-4" data-id="element-2797">
              <label className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" data-id="element-2798">
                <input type="checkbox" checked={verificationChecks.supplierVerified} onChange={() => handleCheckboxChange('supplierVerified')} className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500" data-id="element-2799" />
                <div className="ml-3" data-id="element-2800">
                  <div className="text-sm font-medium text-gray-900" data-id="element-2801">
                    Supplier Information Verified
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-2802">
                    Confirm that the supplier details are correct and the
                    supplier is authorized
                  </div>
                </div>
              </label>
              <label className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" data-id="element-2803">
                <input type="checkbox" checked={verificationChecks.amountVerified} onChange={() => handleCheckboxChange('amountVerified')} className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500" data-id="element-2804" />
                <div className="ml-3" data-id="element-2805">
                  <div className="text-sm font-medium text-gray-900" data-id="element-2806">
                    Amount Verified
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-2807">
                    Confirm that the total amount matches the purchase order or
                    agreement
                  </div>
                </div>
              </label>
              <label className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" data-id="element-2808">
                <input type="checkbox" checked={verificationChecks.itemsVerified} onChange={() => handleCheckboxChange('itemsVerified')} className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500" data-id="element-2809" />
                <div className="ml-3" data-id="element-2810">
                  <div className="text-sm font-medium text-gray-900" data-id="element-2811">
                    Items and Quantities Verified
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-2812">
                    Confirm that all items and quantities are correct
                  </div>
                </div>
              </label>
              <label className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" data-id="element-2813">
                <input type="checkbox" checked={verificationChecks.termsVerified} onChange={() => handleCheckboxChange('termsVerified')} className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500" data-id="element-2814" />
                <div className="ml-3" data-id="element-2815">
                  <div className="text-sm font-medium text-gray-900" data-id="element-2816">
                    Payment Terms Verified
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-2817">
                    Confirm that the payment terms and due date are acceptable
                  </div>
                </div>
              </label>
            </div>
            {!allChecksCompleted && <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg" data-id="element-2818">
                <AlertCircleIcon size={20} className="text-yellow-600 mt-0.5" data-id="element-2819" />
                <div className="text-sm text-yellow-800" data-id="element-2820">
                  Please complete all verification checks before proceeding
                </div>
              </div>}
            <div className="flex justify-between" data-id="element-2821">
              <button onClick={() => setStep(1)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-2822">
                Back
              </button>
              <button onClick={() => setStep(3)} disabled={!allChecksCompleted} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2823">
                Continue to Decision
              </button>
            </div>
          </div>}
        {/* Step 3: Decision */}
        {step === 3 && <div className="space-y-6" data-id="element-2824">
            <div data-id="element-2825">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2826">
                Approval Decision
              </h2>
              <p className="text-gray-600" data-id="element-2827">
                Make your final decision on this bill
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-2828">
              <button onClick={() => setApprovalDecision('approve')} className={`p-6 border-2 rounded-lg transition-all ${approvalDecision === 'approve' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'}`} data-id="element-2829">
                <CheckCircleIcon size={48} className={`mx-auto mb-3 ${approvalDecision === 'approve' ? 'text-green-600' : 'text-gray-400'}`} data-id="element-2830" />
                <div className="text-lg font-semibold text-gray-900" data-id="element-2831">
                  Approve
                </div>
                <div className="text-sm text-gray-500 mt-1" data-id="element-2832">
                  Approve this bill for payment
                </div>
              </button>
              <button onClick={() => setApprovalDecision('reject')} className={`p-6 border-2 rounded-lg transition-all ${approvalDecision === 'reject' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-red-300'}`} data-id="element-2833">
                <XCircleIcon size={48} className={`mx-auto mb-3 ${approvalDecision === 'reject' ? 'text-red-600' : 'text-gray-400'}`} data-id="element-2834" />
                <div className="text-lg font-semibold text-gray-900" data-id="element-2835">
                  Reject
                </div>
                <div className="text-sm text-gray-500 mt-1" data-id="element-2836">
                  Reject this bill and return to supplier
                </div>
              </button>
            </div>
            <div data-id="element-2837">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2838">
                Notes {approvalDecision === 'reject' && '*'}
              </label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} placeholder={approvalDecision === 'reject' ? 'Please provide a reason for rejection...' : 'Add any additional notes (optional)...'} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-2839" />
            </div>
            <div className="flex justify-between" data-id="element-2840">
              <button onClick={() => setStep(2)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-2841">
                Back
              </button>
              <button onClick={handleSubmit} disabled={!approvalDecision || approvalDecision === 'reject' && !notes} className={`px-6 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed ${approvalDecision === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`} data-id="element-2842">
                {approvalDecision === 'approve' ? 'Approve Bill' : 'Reject Bill'}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}